import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
    CartesianGrid,
} from "recharts";
import {
    Megaphone,
    Video,
    Star,
    TrendingUp,
    MousePointerClick,
    Eye,
    Activity,
    ChevronRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { adsAnalytics, listBanners, listAdminVideos, listSponsored } from "@/lib/ads";
import { errorMessage } from "@/lib/api";

function KpiTile({ label, value, sub, icon: Icon, tone = "orange", testid }) {
    const toneCls = {
        orange: "from-orange-500 to-amber-400 text-white",
        emerald: "from-emerald-600 to-emerald-400 text-white",
        blue: "from-blue-600 to-blue-400 text-white",
        rose: "from-rose-600 to-rose-400 text-white",
    }[tone];
    return (
        <Card
            className={`relative overflow-hidden rounded-2xl border-0 bg-gradient-to-br ${toneCls} p-4 shadow-sm`}
            data-testid={testid}
        >
            <div className="flex items-center justify-between opacity-90">
                <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
                <Icon size={18} />
            </div>
            <div className="mt-2 text-3xl font-extrabold leading-none">{value}</div>
            {sub && <div className="mt-1 text-xs opacity-90">{sub}</div>}
        </Card>
    );
}

function TypeBreakdownCard({ title, icon: Icon, data, topByClicks = [], accent = "orange" }) {
    if (!data) return <Skeleton className="h-56 rounded-2xl" />;
    const { count = 0, impressions = 0, clicks = 0 } = data;
    const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(1) : "0.0";
    const accentCls = {
        orange: "text-orange-700 bg-orange-100",
        blue: "text-blue-700 bg-blue-100",
        emerald: "text-emerald-700 bg-emerald-100",
    }[accent];
    return (
        <Card className="rounded-2xl border border-neutral-200 p-5">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${accentCls}`}>
                        <Icon size={16} />
                    </div>
                    <div>
                        <div className="text-sm font-bold text-neutral-900">{title}</div>
                        <div className="text-xs text-neutral-500">{count} total</div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-xs text-neutral-500">CTR</div>
                    <div className="text-lg font-bold text-neutral-900">{ctr}%</div>
                </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl border border-neutral-200 p-3">
                    <div className="text-[10px] uppercase text-neutral-500">Impressions</div>
                    <div className="text-lg font-bold text-neutral-900">{impressions.toLocaleString()}</div>
                </div>
                <div className="rounded-xl border border-neutral-200 p-3">
                    <div className="text-[10px] uppercase text-neutral-500">Clicks</div>
                    <div className="text-lg font-bold text-neutral-900">{clicks.toLocaleString()}</div>
                </div>
            </div>
            {topByClicks.length > 0 && (
                <div className="mt-4">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                        Top by clicks
                    </div>
                    <ul className="mt-2 space-y-1">
                        {topByClicks.slice(0, 3).map((r) => (
                            <li key={r.id} className="flex items-center justify-between text-xs">
                                <span className="min-w-0 truncate text-neutral-800">{r.title || r.target_id?.slice(0, 8)}</span>
                                <span className="ml-2 shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 font-semibold text-neutral-700">
                                    {r.clicks_count || 0}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Card>
    );
}

export default function AdsOverviewPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isSuper = user?.role === "SUPER_ADMIN";
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [banners, setBanners] = useState([]);
    const [videos, setVideos] = useState([]);
    const [sponsored, setSponsored] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);

    const load = async (d) => {
        setLoading(true);
        try {
            const [a, b, v, pending] = await Promise.all([
                adsAnalytics(d),
                listBanners({ limit: 50 }),
                listAdminVideos({ limit: 100 }),
                listAdminVideos({ status: "PENDING_REVIEW", limit: 200 }),
            ]);
            setAnalytics(a);
            setBanners(b);
            setVideos(v);
            setPendingCount((pending || []).length);
            if (isSuper) {
                try {
                    const sp = await listSponsored({ limit: 100 });
                    setSponsored(sp);
                } catch (_) {
                    setSponsored([]);
                }
            }
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(days);
    }, [days]); // eslint-disable-line

    const totals = analytics?.totals || { impressions: 0, clicks: 0 };
    const ctr = totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(1) : "0.0";
    const liveCount = useMemo(() => {
        const b = banners.filter((x) => x.status === "LIVE").length;
        const v = videos.filter((x) => x.status === "LIVE").length;
        const s = sponsored.filter((x) => x.status === "LIVE").length;
        return b + v + s;
    }, [banners, videos, sponsored]);

    const chartData = useMemo(() => {
        const src = analytics || {};
        return [
            { name: "Banners", Impressions: src.banners?.impressions || 0, Clicks: src.banners?.clicks || 0 },
            { name: "Videos", Impressions: src.videos?.impressions || 0, Clicks: src.videos?.clicks || 0 },
            { name: "Sponsored", Impressions: src.sponsored?.impressions || 0, Clicks: src.sponsored?.clicks || 0 },
        ];
    }, [analytics]);

    const topBanners = useMemo(
        () => [...banners].sort((a, b) => (b.clicks_count || 0) - (a.clicks_count || 0)).slice(0, 3),
        [banners],
    );
    const topVideos = useMemo(
        () => [...videos].sort((a, b) => (b.clicks_count || 0) - (a.clicks_count || 0)).slice(0, 3),
        [videos],
    );
    const topSponsored = useMemo(
        () => [...sponsored].sort((a, b) => (b.clicks_count || 0) - (a.clicks_count || 0)).slice(0, 3),
        [sponsored],
    );

    return (
        <div data-testid="ads-overview-page">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-sm font-medium text-orange-600">Monetization</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 sm:text-4xl">
                        Ads Overview
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Banners, video ads and sponsored listings — impressions, clicks & CTR.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                        <SelectTrigger className="w-40" data-testid="ads-days-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">Last 7 days</SelectItem>
                            <SelectItem value="30">Last 30 days</SelectItem>
                            <SelectItem value="60">Last 60 days</SelectItem>
                        </SelectContent>
                    </Select>
                    <Link to="/admin/ads/banners">
                        <Button variant="outline" data-testid="quick-banners-btn">
                            <Megaphone size={14} className="mr-2" /> Create Banner
                        </Button>
                    </Link>
                    <Link to="/admin/ads/videos" className="relative">
                        <Button variant="outline" data-testid="quick-videos-btn">
                            <Video size={14} className="mr-2" /> Review Videos
                            {pendingCount > 0 && (
                                <Badge className="ml-2 h-5 rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white" data-testid="video-queue-pending-count">
                                    {pendingCount}
                                </Badge>
                            )}
                        </Button>
                    </Link>
                    {isSuper && (
                        <Link to="/admin/ads/sponsored">
                            <Button className="nest-gradient text-white" data-testid="quick-sponsored-btn">
                                <Star size={14} className="mr-2" /> New Sponsored
                            </Button>
                        </Link>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiTile
                    label="Impressions"
                    value={(totals.impressions || 0).toLocaleString()}
                    sub={`in last ${days} days`}
                    icon={Eye}
                    tone="orange"
                    testid="kpi-impressions"
                />
                <KpiTile
                    label="Clicks"
                    value={(totals.clicks || 0).toLocaleString()}
                    sub={`CTR ${ctr}%`}
                    icon={MousePointerClick}
                    tone="emerald"
                    testid="kpi-clicks"
                />
                <KpiTile
                    label="CTR"
                    value={`${ctr}%`}
                    sub="All ad types"
                    icon={TrendingUp}
                    tone="blue"
                    testid="kpi-ctr"
                />
                <KpiTile
                    label="Live ads"
                    value={liveCount}
                    sub="Banners + videos + sponsored"
                    icon={Activity}
                    tone="rose"
                    testid="kpi-live"
                />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <TypeBreakdownCard
                    title="Banner Ads"
                    icon={Megaphone}
                    data={analytics?.banners}
                    topByClicks={topBanners}
                    accent="orange"
                />
                <TypeBreakdownCard
                    title="Video Ads"
                    icon={Video}
                    data={analytics?.videos}
                    topByClicks={topVideos}
                    accent="blue"
                />
            </div>
            <div className="mt-4">
                <TypeBreakdownCard
                    title="Sponsored Listings"
                    icon={Star}
                    data={analytics?.sponsored}
                    topByClicks={topSponsored}
                    accent="emerald"
                />
            </div>

            <Card className="mt-6 rounded-2xl border border-neutral-200 p-5">
                <div className="mb-3 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-bold text-neutral-900">
                            Impressions vs Clicks
                        </div>
                        <div className="text-xs text-neutral-500">
                            Across ad types · last {days} days
                        </div>
                    </div>
                </div>
                <div className="h-64">
                    {loading && !analytics ? (
                        <Skeleton className="h-full w-full rounded-xl" />
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis dataKey="name" fontSize={12} />
                                <YAxis fontSize={12} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="Impressions" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                                <Bar dataKey="Clicks" fill="#0284c7" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </Card>
        </div>
    );
}
